<template>
    <div>
        <!-- Unsaved changes message -->
        <div
            v-if="hasUnsavedChanges"
            class="unsaved-changes-message"
        >
            <span class="unsaved-changes-icon">!</span>

            <span>
                {{ __('settings.contract_reminders_settings.unsaved_changes') }}
            </span>
        </div>

        <!-- Contract-reminders-settings -->
        <DCard padding>
            <div class="setting-header">
                <div>
                    <h2>
                        {{ __('settings.contract_reminders_settings.contract_reminders_settings') }}
                    </h2>

                    <p>
                        {{ __('settings.contract_reminders_settings.contract_reminders_settings_info') }}
                    </p>
                </div>

                <div class="setting-toggle">
                    <DSwitch
                        v-model:checked="is_enabled"
                        size="xl"
                    />
                </div>
            </div>
        </DCard>

        <!-- Maximum age -->
        <DCard padding>
            <div class="maximum-age-section">
                <h2>
                    {{ __('settings.contract_reminders_settings.maximum_age') }}
                </h2>

                <p>
                    {{ __('settings.contract_reminders_settings.maximum_age_description') }}
                </p>

                <div class="input-with-suffix">
                    <DInput
                        id="max_contract_age_days"
                        v-model="max_contract_age_days"
                        type="number"
                        :is-valid="isMaxContractAgeValid"
                        :disabled="!is_enabled"
                    />

                    <span>
                        {{ __('settings.contract_reminders_settings.days') }}
                    </span>
                </div>
            </div>

            <div class="info-message">
                <span class="info-icon">i</span>

                <span>
                    {{ __('settings.contract_reminders_settings.maximum_age_info') }}
                </span>
            </div>

            <div
                v-if="maxContractAgeError"
                class="validation-error"
            >
                {{ maxContractAgeError }}
            </div>
        </DCard>

        <!-- Reminder schedule -->
        <DCard padding>
            <div class="section-title">
                <h2>
                    {{ __('settings.contract_reminders_settings.reminder_schedule') }}
                </h2>

                <p>
                    {{ __('settings.contract_reminders_settings.reminder_schedule_info') }}
                </p>
            </div>

            <div class="schedule-fields">
                <DFormRow
                    :label="__('settings.contract_reminders_settings.reminder_sequence')"
                    is-required
                    :error="errors.reminder_days"
                >
                    <DInput
                        id="reminder_days"
                        v-model="reminder_days"
                        :placeholder="defaultReminderDays.join(', ')"
                        :is-valid="isReminderDaysValid"
                        :disabled="!is_enabled"
                    />

                    <small>
                        {{ __('settings.contract_reminders_settings.first_reminder_day_info') }}
                    </small>

                    <small>
                        {{ __('settings.contract_reminders_settings.second_reminder_day_info') }}
                    </small>

                    <div
                        v-if="reminderDaysError"
                        class="validation-error"
                    >
                        {{ reminderDaysError }}
                    </div>
                </DFormRow>

                <DFormRow
                    :label="__('settings.contract_reminders_settings.final_reminder_day')"
                    is-required
                    :error="errors.final_reminder_day"
                >
                    <div class="input-with-suffix">
                        <DInput
                            id="final_reminder_day"
                            v-model="final_reminder_day"
                            type="number"
                            :is-valid="isFinalReminderDayValid"
                            :disabled="!is_enabled"
                        />

                        <span>
                            {{ __('settings.contract_reminders_settings.days') }}
                        </span>
                    </div>

                    <div
                        v-if="finalReminderDayError"
                        class="validation-error"
                    >
                        {{ finalReminderDayError }}
                    </div>
                </DFormRow>
            </div>

            <div class="info-message">
                <span class="info-icon">i</span>

                <span>
                    {{ __('settings.contract_reminders_settings.reminder_schedule_info_message') }}
                </span>
            </div>
        </DCard>

        <!-- Save button -->
        <DFormControls
            @submit="saveSettings"
        />

        <!-- Unsaved changes modal -->
        <DModal
            v-model:show="showUnsavedChangesModal"
            :title="__('settings.contract_reminders_settings.unsaved_changes_title')"
            :message="__('settings.contract_reminders_settings.unsaved_changes_message')"
            is-error
            is-confirm
            :is-awaiting-validation="isSavingBeforeNavigation"
            @confirm="proceedWithNavigation"
            @close="cancelNavigation"
        />
    </div>
</template>

<script>
export default {
    name: 'Create',

    props: {
        errors: {
            type: Object,
            default () {
                return {}
            }
        },

        setting: {
            type: Object,
            default () {
                return {}
            }
        },
        defaultReminderDays: {
            type: Array,
            default () {
                return []
            }
        }
    },

    data () {
        return {
            is_enabled: this.setting.is_enabled ?? false,

            max_contract_age_days:
                this.setting.max_contract_age_days ?? 90,

            reminder_days: Array.isArray(this.setting.reminder_days)
                ? this.setting.reminder_days.join(', ')
                : this.defaultReminderDays.join(', '),

            final_reminder_day:
                this.setting.final_reminder_day ?? 60,

            /*
             * Original values loaded from the server.
             */
            initialFormState: null,

            /*
             * Unsaved changes modal.
             */
            showUnsavedChangesModal: false,

            /*
             * Inertia visit that the user attempted.
             */
            pendingVisit: null,

            /*
             * Prevent multiple save requests.
             */
            isSavingBeforeNavigation: false,

            /*
             * Allows the Inertia visit after the
             * settings have been successfully saved.
             */
            allowNavigation: false,

            /*
             * Inertia event cleanup function.
             */
            removeInertiaBeforeListener: null
        }
    },

    computed: {
        currentFormState () {
            return {
                is_enabled: this.is_enabled,
                max_contract_age_days: this.max_contract_age_days,
                reminder_days: this.reminder_days,
                final_reminder_day: this.final_reminder_day
            }
        },

        hasUnsavedChanges () {
            if (!this.initialFormState) {
                return false
            }

            return (
                JSON.stringify(this.currentFormState) !==
                JSON.stringify(this.initialFormState)
            )
        },

        parsedReminderDays () {
            if (!this.reminder_days.trim()) {
                return []
            }

            return this.reminder_days
                .split(',')
                .map(day => day.trim())
                .filter(day => day !== '')
                .map(day => Number(day))
        },

        hasInvalidReminderDay () {
            if (!this.reminder_days.trim()) {
                return false
            }

            return this.reminder_days
                .split(',')
                .some(day => {
                    const value = day.trim()

                    if (value === '') {
                        return false
                    }

                    return !/^\d+$/.test(value)
                })
        },

        isMaxContractAgeValid () {
            if (!this.is_enabled) {
                return true
            }

            const maxAge = Number(this.max_contract_age_days)

            return (
                Number.isInteger(maxAge) &&
                maxAge > 0 &&
                maxAge <= 90
            )
        },

        isFinalReminderDayValid () {
            if (!this.is_enabled) {
                return true
            }

            const finalDay = Number(this.final_reminder_day)
            const maxAge = Number(this.max_contract_age_days)

            return (
                Number.isInteger(finalDay) &&
                finalDay > 0 &&
                Number.isInteger(maxAge) &&
                maxAge > 0 &&
                finalDay <= maxAge - 1
            )
        },

        isReminderDaysValid () {
            if (!this.is_enabled) {
                return true
            }

            const days = this.parsedReminderDays
            const finalDay = Number(this.final_reminder_day)

            if (this.hasInvalidReminderDay) {
                return false
            }

            if (!days.length) {
                return false
            }

            if (
                days.some(
                    day => !Number.isInteger(day) || day <= 0
                )
            ) {
                return false
            }

            if (new Set(days).size !== days.length) {
                return false
            }

            for (let i = 1; i < days.length; i++) {
                if (days[i] <= days[i - 1]) {
                    return false
                }
            }

            if (days.some(day => day >= finalDay)) {
                return false
            }

            return true
        },

        isFormValid () {
            if (!this.is_enabled) {
                return true
            }

            return (
                this.isMaxContractAgeValid &&
                this.isReminderDaysValid &&
                this.isFinalReminderDayValid
            )
        },

        maxContractAgeError () {
            if (!this.is_enabled) {
                return null
            }

            const maxAge = Number(this.max_contract_age_days)

            if (!this.max_contract_age_days) {
                return this.__(
                    'settings.contract_reminders_settings.form_maximum_age_required'
                )
            }

            if (!Number.isInteger(maxAge)) {
                return this.__(
                    'settings.contract_reminders_settings.form_maximum_age_integer'
                )
            }

            if (maxAge > 90) {
                return this.__(
                    'settings.contract_reminders_settings.form_maximum_age_hint'
                )
            }

            if (maxAge < 1) {
                return this.__(
                    'settings.contract_reminders_settings.form_minimum_age_required'
                )
            }

            return null
        },

        finalReminderDayError () {
            if (!this.is_enabled) {
                return null
            }

            const finalDay = Number(this.final_reminder_day)
            const maxAge = Number(this.max_contract_age_days)

            if (!this.final_reminder_day) {
                return this.__(
                    'settings.contract_reminders_settings.form_final_reminder_required'
                )
            }

            if (!Number.isInteger(finalDay)) {
                return this.__(
                    'settings.contract_reminders_settings.form_final_reminder_integer'
                )
            }

            if (finalDay < 1) {
                return this.__(
                    'settings.contract_reminders_settings.form_final_reminder_greater'
                )
            }

            if (finalDay >= maxAge) {
                return this.__(
                    'settings.contract_reminders_settings.form_greater_than_maximum_age'
                )
            }

            return null
        },

        reminderDaysError () {
            if (!this.is_enabled) {
                return null
            }

            const days = this.parsedReminderDays
            const finalDay = Number(this.final_reminder_day)
            const maxAge = Number(this.max_contract_age_days)

            if (!this.reminder_days.trim()) {
                return this.__(
                    'settings.contract_reminders_settings.reminder_days_required'
                )
            }

            if (this.hasInvalidReminderDay) {
                return this.__(
                    'settings.contract_reminders_settings.reminder_days_integer'
                )
            }

            if (days.some(day => day <= 0)) {
                return this.__(
                    'settings.contract_reminders_settings.reminder_days_greater'
                )
            }

            if (new Set(days).size !== days.length) {
                return this.__(
                    'settings.contract_reminders_settings.reminder_days_unique'
                )
            }

            for (let i = 1; i < days.length; i++) {
                if (days[i] <= days[i - 1]) {
                    return this.__(
                        'settings.contract_reminders_settings.reminder_days_ascending_order'
                    )
                }
            }

            if (days.some(day => day > maxAge)) {
                return this.__(
                    'settings.contract_reminders_settings.reminder_days_maximum_age'
                )
            }

            if (days.some(day => day >= finalDay)) {
                return this.__(
                    'settings.contract_reminders_settings.reminder_days_final_reminder_day'
                )
            }

            return null
        }
    },

    mounted () {
        /*
         * Save the original server state.
         */
        this.setInitialFormState()

        /*
         * Browser refresh / close protection.
         *
         * This is completely separate from Inertia
         * navigation.
         */
        window.addEventListener(
            'beforeunload',
            this.handleBeforeUnload
        )

        /*
         * Listen to Inertia navigation.
         *
         * This catches Inertia <Link> navigation and
         * router/inertia visits without listening to
         * document clicks.
         */
        this.removeInertiaBeforeListener =
            this.$inertia.on('before', event => {
                const visit = event.detail.visit

                // Do not intercept form submissions such as Save.
                if (visit.method !== 'get') {
                    return
                }

                if (this.allowNavigation) {
                    this.allowNavigation = false

                    return
                }

                if (!this.hasUnsavedChanges) {
                    return
                }

                event.preventDefault()

                this.pendingVisit = visit

                this.showUnsavedChangesModal = true
            })
    },

    beforeUnmount () {
        /*
         * Remove browser refresh protection.
         */
        window.removeEventListener(
            'beforeunload',
            this.handleBeforeUnload
        )

        /*
         * Remove the Inertia listener.
         */
        if (this.removeInertiaBeforeListener) {
            this.removeInertiaBeforeListener()

            this.removeInertiaBeforeListener = null
        }
    },

    methods: {
        /*
         * -----------------------------------
         * Form state
         * -----------------------------------
         */

        setInitialFormState () {
            this.initialFormState = JSON.parse(
                JSON.stringify(this.currentFormState)
            )
        },

        /*
         * -----------------------------------
         * Browser refresh / close
         * -----------------------------------
         */

        handleBeforeUnload (event) {
            if (!this.hasUnsavedChanges) {
                return
            }

            /*
             * Modern browsers require returnValue.
             *
             * The browser will show its own native
             * "Leave site?" confirmation.
             */
            event.preventDefault()
            event.returnValue = ''
        },

        /*
         * -----------------------------------
         * Cancel Inertia navigation
         * -----------------------------------
         */

        cancelNavigation () {
            this.showUnsavedChangesModal = false
            this.pendingVisit = null
            this.isSavingBeforeNavigation = false
        },

        /*
         * -----------------------------------
         * Confirm Inertia navigation
         *
         * Save first, then continue with the
         * original Inertia visit.
         * -----------------------------------
         */

        proceedWithNavigation () {
            if (!this.pendingVisit) {
                this.showUnsavedChangesModal = false

                return
            }

            /*
             * Do not save invalid form data.
             */
            if (!this.isFormValid) {
                return
            }

            if (this.isSavingBeforeNavigation) {
                return
            }

            this.isSavingBeforeNavigation = true

            const visit = this.pendingVisit

            /*
             * Save the settings first.
             */
            this.$inertia.post(
                this.$route(
                    'settings.contract-reminders-settings.store'
                ),
                {
                    is_enabled: this.is_enabled,

                    max_contract_age_days:
                        Number(
                            this.max_contract_age_days
                        ),

                    reminder_days:
                    this.parsedReminderDays,

                    final_reminder_day:
                        Number(
                            this.final_reminder_day
                        )
                },
                {
                    preserveScroll: true,

                    onSuccess: () => {
                        /*
                         * The form is now saved.
                         */
                        this.setInitialFormState()

                        /*
                         * Close the modal.
                         */
                        this.showUnsavedChangesModal = false

                        this.isSavingBeforeNavigation = false

                        this.pendingVisit = null

                        /*
                         * Allow the original Inertia
                         * navigation.
                         */
                        this.allowNavigation = true

                        /*
                         * Continue the exact visit the
                         * user originally requested.
                         */
                        this.$inertia.visit(
                            visit.url,
                            {
                                method: visit.method,
                                data: visit.data,
                                replace: visit.replace,
                                preserveScroll:
                                visit.preserveScroll,
                                preserveState:
                                visit.preserveState,
                                only: visit.only,
                                headers: visit.headers,
                                errorBag: visit.errorBag,
                                forceFormData:
                                visit.forceFormData,
                                queryStringArrayFormat:
                                visit.queryStringArrayFormat
                            }
                        )
                    },

                    onError: () => {
                        /*
                         * Stay on the current page.
                         */
                        this.isSavingBeforeNavigation = false
                    },

                    onCancel: () => {
                        this.isSavingBeforeNavigation = false
                    }
                }
            )
        },

        /*
         * -----------------------------------
         * Normal save button
         * -----------------------------------
         */

        saveSettings () {
            if (!this.isFormValid) {
                return
            }

            this.$inertia.post(
                this.$route(
                    'settings.contract-reminders-settings.store'
                ),
                {
                    is_enabled: this.is_enabled,

                    max_contract_age_days:
                        Number(
                            this.max_contract_age_days
                        ),

                    reminder_days:
                    this.parsedReminderDays,

                    final_reminder_day:
                        Number(
                            this.final_reminder_day
                        )
                },
                {
                    onSuccess: () => {
                        /*
                         * The server now contains the
                         * current form values.
                         */
                        this.setInitialFormState()

                        this.allowNavigation = false
                        this.pendingVisit = null
                    }
                }
            )
        }
    }
}
</script>

<style scoped>
.setting-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.setting-header h2,
.section-title h2 {
    margin: 0 0 8px;
}

.setting-header p,
.section-title p {
    margin: 0;
}

.setting-toggle {
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.input-with-suffix {
    display: flex;
    align-items: center;
    gap: 12px;
}

.input-with-suffix :deep(input) {
    width: 50%;
}

.input-with-suffix span {
    white-space: nowrap;
}

.schedule-fields {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 32px;
}

.schedule-fields > :deep(.d-form-row) {
    display: block;
}

.schedule-fields > :deep(.d-form-row > label) {
    display: block;
    margin-bottom: 8px;
}

.info-message {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 16px;
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
}

.info-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: 1px solid currentColor;
    border-radius: 50%;
    font-weight: bold;
    font-size: 12px;
}

.validation-error {
    margin-top: 6px;
    color: #dc2626;
    font-size: 14px;
}

small {
    display: block;
    margin-top: 6px;
}

:deep(.d-form-row) {
    display: block;
}

:deep(.d-form-row > label) {
    display: block;
    margin-bottom: 8px;
}

/*
 * Unsaved changes indicator
 */
.unsaved-changes-message {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding: 10px 14px;
    color: #92400e;
    background-color: #fffbeb;
    border: 1px solid #f59e0b;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
}

.unsaved-changes-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    color: #92400e;
    border: 1px solid #f59e0b;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
}
</style>
